import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';

function TableColumnToggle({ columns = [], hidden = [], requiredIds = [], onToggle, onReset }) {
  const popover = usePopover();

  const hideable = useMemo(() => {
    const required = new Set(requiredIds);
    return columns.filter((col) => !required.has(col.id));
  }, [columns, requiredIds]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);

  return (
    <>
      <Button
        color="inherit"
        variant="outlined"
        onClick={popover.onOpen}
        startIcon={<Iconify icon="solar:widget-5-bold" width={18} />}
        sx={{ flexShrink: 0, height: 54 }}
      >
        Columns
      </Button>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 260, p: 0 }}>
        <Stack sx={{ p: 1.5, pb: 0.5 }}>
          <Typography variant="subtitle2">Visible columns</Typography>
        </Stack>

        <Stack sx={{ px: 1, py: 0.5, maxHeight: 320, overflowY: 'auto' }}>
          {hideable.map((col) => (
            <MenuItem key={col.id} disableRipple sx={{ p: 0, '&:hover': { bgcolor: 'transparent' } }}>
              <FormControlLabel
                sx={{ px: 1, width: 1, m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={!hiddenSet.has(col.id)}
                    onChange={() => onToggle(col.id)}
                  />
                }
                label={col.label}
              />
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          <Button size="small" color="inherit" onClick={onReset}>
            Reset layout
          </Button>
        </Stack>
      </CustomPopover>
    </>
  );
}

TableColumnToggle.propTypes = {
  columns: PropTypes.array,
  hidden: PropTypes.array,
  requiredIds: PropTypes.array,
  onToggle: PropTypes.func,
  onReset: PropTypes.func,
};

export default memo(TableColumnToggle);
